import type { FC } from 'react';
import { useCallback, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type { SavePromptOffer } from 'src/components/SupportCase/types';

/** Where a "Not now" is remembered. One key per question, not per user. */
export const DISMISS_KEY_PREFIX = 'pronto.support.promptNudge.';

export const isDismissed = (bodyKey: string): boolean => {
  try {
    return (
      window.localStorage.getItem(`${DISMISS_KEY_PREFIX}${bodyKey}`) === '1'
    );
  } catch {
    // Private mode, or storage disabled. A nudge is not worth an exception.
    return false;
  }
};

export const rememberDismissal = (bodyKey: string): void => {
  try {
    window.localStorage.setItem(`${DISMISS_KEY_PREFIX}${bodyKey}`, '1');
  } catch {
    // As above: failing to remember means they see the offer again, nothing worse.
  }
};

interface SaveNudgeProps {
  offer: SavePromptOffer;
  /** Save with the chosen title. `ok: false` carries the server's sentence. */
  onSave: (title: string) => Promise<{ ok: boolean; detail?: string }>;
  /** Called after a save or a dismissal, so the caller can unmount this. */
  onDone: () => void;
}

/**
 * "You've asked this a few times. Save it as a prompt?" (ENG-4220)
 *
 * One line, two answers, and no dialog: a modal for a five-character decision is a
 * tax. Pressing Save turns the line into a title field pre-filled with the question,
 * because the title is what the chip will say and it should be short.
 *
 * "Not now" is remembered in `localStorage` and nowhere else. A cross-device dismissal
 * needs a table, a migration and a sync rule; a nudge does not earn one, and the cost
 * of being wrong is one more offer on another machine.
 */
const SaveNudge: FC<SaveNudgeProps> = ({ offer, onSave, onDone }) => {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const defaultTitle = useMemo(
    () => offer.body.slice(0, 60).trim(),
    [offer.body]
  );

  const startEditing = useCallback(() => {
    setTitle(defaultTitle);
    setEditing(true);
  }, [defaultTitle]);

  const dismiss = useCallback(() => {
    rememberDismissal(offer.body_key);
    onDone();
  }, [offer.body_key, onDone]);

  const submit = useCallback(async () => {
    setSaving(true);
    setError(null);
    const result = await onSave(title.trim() || defaultTitle);
    setSaving(false);
    if (result.ok) {
      onDone();
      return;
    }
    setError(result.detail || 'That prompt could not be saved.');
  }, [defaultTitle, onSave, onDone, title]);

  return (
    <Stack spacing={1} sx={{ pt: 0.5 }} data-testid="save-prompt-nudge">
      {editing ? (
        <>
          <TextField
            size="small"
            fullWidth
            label="Name this prompt"
            value={title}
            onChange={(event) => setTitle(event.target.value.slice(0, 60))}
            inputProps={{ maxLength: 60, 'aria-label': 'Prompt name' }}
            autoFocus
          />
          {error ? (
            <Alert severity="error" sx={{ py: 0 }}>
              {error}
            </Alert>
          ) : null}
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="contained"
              color="info"
              onClick={() => void submit()}
              disabled={saving}
            >
              Save prompt
            </Button>
            <Button
              size="small"
              onClick={() => setEditing(false)}
              disabled={saving}
            >
              Cancel
            </Button>
          </Stack>
        </>
      ) : (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="caption" color="text.secondary">
            You&apos;ve asked this a few times. Save it as a prompt?
          </Typography>
          <Stack direction="row" spacing={0.5}>
            <Button size="small" onClick={startEditing}>
              Save
            </Button>
            <Button size="small" color="inherit" onClick={dismiss}>
              Not now
            </Button>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};

export default SaveNudge;
